import { CM_TEMPLATE_COLUMNS } from '@felix/domain';
import { Alert, Button, FileButton, Loader, Modal } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconFileUpload, IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Condition.module.css';
import type { CmApplied, CmReport } from './condition-types.js';
import { cmCanApply } from './condition-view.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import { CmImportPreview } from './ImportPreview.js';
import work from './PmsWork.module.css';
import type { CmImporter } from './source.js';

/** Gemi sunucusu ve bulut sınırı (`TABLE_FILE_LIMIT`). */
export const CM_FILE_LIMIT = 10 * 1024 * 1024;

/** Seçilen dosyanın ön denetimi: boyut ve uzantı (türü sunucu içerikten yeniden bulur). */
export function cmFileProblem(file: { name: string; size: number }): string | null {
  if (file.size === 0) return 'EMPTY_FILE';
  if (file.size > CM_FILE_LIMIT) return 'FILE_TOO_LARGE';
  if (!/\.(csv|xlsx)$/i.test(file.name)) return 'UNSUPPORTED_FILE_TYPE';
  return null;
}

export type CmImportState =
  | { kind: 'idle' }
  | { kind: 'previewing'; fileName: string }
  | { kind: 'preview'; fileName: string; report: CmReport }
  | { kind: 'applying'; fileName: string; report: CmReport }
  | { kind: 'applied'; fileName: string; applied: CmApplied }
  | { kind: 'error'; fileName: string | null; error: unknown };

/**
 * Diyaloğun gövdesi (bileşen testi sunucu çizimiyle): şablon ve dosya seçimi, önizleme raporu,
 * "Import" (yalnız satır hatası yokken), sonuç.
 */
export function CmImportContent({
  scope,
  state,
  pointNames = new Map(),
  onFile,
  onApply,
}: {
  scope: 'web' | 'ship';
  state: CmImportState;
  pointNames?: ReadonlyMap<string, string>;
  onFile: (file: File) => void;
  onApply: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.cm.${key}`, options);
  const fileName = 'fileName' in state ? state.fileName : null;
  const busy = state.kind === 'previewing' || state.kind === 'applying';
  const report = state.kind === 'preview' || state.kind === 'applying' ? state.report : null;
  return (
    <div className={classes.stack}>
      <p className={work.dialogBody}>{k('intro')}</p>
      {scope === 'web' ? (
        <Alert color="blue" role="note">
          {k('introOffice')}
        </Alert>
      ) : null}
      <p className={classes.columns}>{`${k('columns')}: ${CM_TEMPLATE_COLUMNS.join(', ')}`}</p>
      <div className={classes.fileRow}>
        <FileButton
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(file) => {
            if (file) onFile(file);
          }}
          disabled={busy}
        >
          {(props) => (
            <Button {...props} variant="default" leftSection={<IconFileUpload size={16} />}>
              {fileName ? k('chooseAgain') : k('choose')}
            </Button>
          )}
        </FileButton>
        {fileName ? <span className={classes.fileName}>{fileName}</span> : null}
        {state.kind === 'previewing' ? (
          <span className={work.note} role="status">
            <Loader size="xs" mr={6} />
            {k('previewing')}
          </span>
        ) : null}
      </div>
      {state.kind === 'error' ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
          {errorCodeOf(state.error) === 'FILE_TOO_LARGE' ? k('tooLarge') : errorText(state.error)}
        </Alert>
      ) : null}
      {report ? <CmImportPreview report={report} pointNames={pointNames} /> : null}
      {state.kind === 'applied' ? (
        <Alert color="green" icon={<IconCircleCheck size={16} />} role="status">
          {k('applied', {
            created: state.applied.report.pointsToCreate.length,
            updated: state.applied.report.pointsToUpdate.length,
            measurements: state.applied.report.measurements.total,
          })}
          {state.applied.defects && state.applied.defects.length > 0
            ? ` ${k('appliedDefects', {
                count: state.applied.defects.length,
                numbers: state.applied.defects.map((d) => d.defectNo).join(', '),
              })}`
            : ''}
        </Alert>
      ) : null}
      {report ? (
        <div className={work.dialogActions}>
          <Button
            leftSection={<IconUpload size={16} />}
            disabled={!cmCanApply(report) || busy}
            loading={state.kind === 'applying'}
            onClick={onApply}
          >
            {k('apply')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function errorCodeOf(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * "Import CM data" (Maintenance Plan başlığı; plan M7 Task 21a; K20) [NR496 4.4.2d]: dosya seçimi,
 * önizleme raporu (satır hataları, eşik değişiklikleri, açılacak arızalar) ve uygulama. Gemide eşik
 * ve değer, ofiste kurulum devrinde yalnız eşik.
 */
export function CmImportDialog({
  opened,
  scope,
  vesselId,
  importer,
  pointNames,
  onClose,
  onApplied,
}: {
  opened: boolean;
  scope: 'web' | 'ship';
  vesselId: string;
  importer: CmImporter;
  pointNames: ReadonlyMap<string, string>;
  onClose: () => void;
  onApplied: () => void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<CmImportState>({ kind: 'idle' });
  const [apply, setApply] = useState<(() => Promise<CmApplied>) | null>(null);
  useEffect(() => {
    if (opened) return;
    setState({ kind: 'idle' });
    setApply(null);
  }, [opened]);
  const onFile = async (file: File) => {
    const problem = cmFileProblem(file);
    if (problem) {
      setState({ kind: 'error', fileName: file.name, error: { code: problem } });
      return;
    }
    setState({ kind: 'previewing', fileName: file.name });
    setApply(null);
    try {
      const preview = await importer.preview(vesselId, file);
      setApply(() => preview.apply);
      setState({ kind: 'preview', fileName: preview.fileName, report: preview.report });
    } catch (error) {
      setState({ kind: 'error', fileName: file.name, error });
    }
  };
  const onApply = async () => {
    if (!apply || state.kind !== 'preview') return;
    setState({ ...state, kind: 'applying' });
    try {
      const applied = await apply();
      setApply(null);
      setState({ kind: 'applied', fileName: state.fileName, applied });
      onApplied();
    } catch (error) {
      setState({ kind: 'error', fileName: state.fileName, error });
    }
  };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={t('pms.condition.cm.title')}
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <CmImportContent
        scope={scope}
        state={state}
        pointNames={pointNames}
        onFile={(file) => void onFile(file)}
        onApply={() => void onApply()}
      />
    </Modal>
  );
}
